import * as React from 'react';
import { Archive, Edit3, Plus, RotateCcw } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableFilterTrigger,
  DashboardTableSearchInput,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  DashboardTableStatusTone,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { formatDisplayDate, formatUsd, numberFormat } from '../../../../../lib/format';
import { dashboardT } from '../../../../../lib/messages';
import { reportMetricLabel } from '../../../../reporting/reportExplorer/format';
import { ReportMetric } from '../../../../reporting/reportExplorer/reportEnums';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useNetworkMutations, useNetworksQuery, useNetworkStatusCountsQuery } from '../../hooks';
import {
  isNetworkSortProperty,
  NetworkCpmMetric,
  NetworkCostType,
  networkPageSizes,
  NetworkSortDirection,
  NetworkStatus,
  resolveNetworkCreatedBy,
  type Network,
} from '../../model';
import { useNetworksRouteFilters } from '../../useNetworksRouteFilters';

const statusOptions = Object.values(NetworkStatus);

export function NetworksTable() {
  const navigate = useNavigate();
  const { can } = useShigoPermissions();
  const { filter, setPagination, setSearch, setSort, setStatuses } = useNetworksRouteFilters();
  const query = useNetworksQuery(filter);
  const counts = useNetworkStatusCountsQuery(filter.searchTerm);
  const mutations = useNetworkMutations();
  const [statusPendingIds, setStatusPendingIds] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const search = useDebouncedValue(filter.searchTerm, setSearch, 650);

  const canCreate = can(ShigoPermission.NetworkCreate);
  const canEdit = can(ShigoPermission.NetworkEdit);
  const canArchive = can(ShigoPermission.NetworkArchive);
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<Network>([
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('networks.name'),
          headerClassName: 'w-64 pl-4',
          className: 'w-64 pl-4',
          sortable: true,
          sortKey: 'name',
          cell: (network) => (
            <button
              type="button"
              className={dashboardTableTextActionClassName()}
              onClick={() => {
                void navigate({
                  to: '/networks/$networkId/details/overview',
                  params: { networkId: network.id },
                });
              }}
            >
              {network.name}
            </button>
          ),
        },
        {
          id: 'networkId',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('networks.networkId'),
          headerClassName: 'w-40',
          className: 'w-40 font-mono text-xs',
          value: (network) => network.id,
        },
        {
          id: 'status',
          kind: DashboardDataTableColumnKind.Status,
          header: dashboardT('networks.status'),
          headerClassName: 'w-28',
          className: 'w-28',
          label: (network) => statusLabel(network.status),
          tone: (network) =>
            network.status === NetworkStatus.Active
              ? DashboardTableStatusTone.Success
              : DashboardTableStatusTone.Neutral,
        },
        {
          id: 'saasType',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('networks.saasType'),
          headerClassName: 'w-36',
          className: 'w-36',
          value: (network) => costTypeLabel(network.saasCost?.costType),
        },
        {
          id: 'saasMetric',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('networks.saasMetric'),
          headerClassName: 'w-40',
          className: 'w-40',
          value: (network) => formatCpmMetric(network.saasCost?.cpmMetric),
        },
        {
          id: 'saasValue',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('networks.saasValue'),
          headerClassName: 'w-28',
          className: 'w-28',
          value: (network) => formatCostValue(network),
        },
        {
          id: 'createdBy',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('networks.createdBy'),
          headerClassName: 'w-48',
          className: 'w-48',
          sortable: true,
          sortKey: 'createdBy',
          value: resolveNetworkCreatedBy,
        },
        {
          id: 'createdOn',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('networks.created'),
          headerClassName: 'w-36',
          className: 'w-36',
          sortable: true,
          sortKey: 'createdOn',
          value: (network) => formatDisplayDate(network.createdOn),
        },
        {
          id: 'updatedOn',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('networks.updated'),
          headerClassName: 'w-36',
          className: 'w-36',
          sortable: true,
          sortKey: 'updatedOn',
          value: (network) => formatDisplayDate(network.updatedOn),
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          cell: (network) => {
            const pending = statusPendingIds.has(network.id);
            const active = network.status === NetworkStatus.Active;
            return (
              <DashboardTableRowActions
                label={dashboardT('networks.actionsFor', { name: network.name })}
                pending={pending}
              >
                {canEdit ? (
                  <DashboardTableRowActionItem
                    onSelect={() => {
                      void navigate({
                        to: '/networks/edit/$networkId',
                        params: { networkId: network.id },
                      });
                    }}
                  >
                    <Edit3 className="h-4 w-4" aria-hidden="true" />
                    {dashboardT('networks.edit')}
                  </DashboardTableRowActionItem>
                ) : null}
                {canArchive ? (
                  <DashboardTableRowActionItem
                    onSelect={() => {
                      setStatusPendingIds((current) => new Set(current).add(network.id));
                      void mutations.setStatus
                        .mutateAsync({ id: network.id, active: !active })
                        .then(() => {
                          toast.success(
                            dashboardT(
                              active ? 'networks.archivedSuccess' : 'networks.activatedSuccess',
                            ),
                          );
                        })
                        .catch((error: unknown) => {
                          showRequestError(error, dashboardT('networks.statusError'));
                        })
                        .finally(() => {
                          setStatusPendingIds((current) => {
                            const next = new Set(current);
                            next.delete(network.id);
                            return next;
                          });
                        });
                    }}
                  >
                    {active ? (
                      <Archive className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    )}
                    {dashboardT(active ? 'networks.archive' : 'networks.activate')}
                  </DashboardTableRowActionItem>
                ) : null}
              </DashboardTableRowActions>
            );
          },
        },
      ]),
    [canArchive, canEdit, mutations.setStatus, navigate, statusPendingIds],
  );

  const selectedStatuses = new Set(filter.statuses);
  return (
    <main className="page-gutter min-w-0 py-6">
      <DashboardDataTable
        title={dashboardT('networks.title')}
        tableLabel={dashboardT('networks.title')}
        columns={columns}
        items={filter.statuses.length ? (query.data?.content ?? []) : []}
        getRowKey={(network) => network.id}
        loading={query.isFetching}
        loadingMessage={dashboardT('networks.loading')}
        emptyMessage={dashboardT('networks.empty')}
        errorMessage={query.isError ? dashboardT('networks.loadError') : undefined}
        minWidthClassName="min-w-[1400px]"
        sort={{
          value: filter.sortProperty
            ? {
                key: filter.sortProperty,
                direction:
                  filter.sortDirection === NetworkSortDirection.Asc
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (sort) => {
            if (!sort) {
              setSort(null, filter.sortDirection);
              return;
            }
            if (!isNetworkSortProperty(sort.key)) return;
            setSort(
              sort.key,
              sort.direction === DashboardDataTableSortDirection.Asc
                ? NetworkSortDirection.Asc
                : NetworkSortDirection.Desc,
            );
          },
        }}
        toolbar={
          <>
            <DashboardTableSearchInput
              value={search.draft}
              aria-label={dashboardT('networks.search')}
              placeholder={dashboardT('networks.search')}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <DashboardTableFilterTrigger
                  className="w-44"
                  label={statusFilterLabel(filter.statuses)}
                  aria-label={dashboardT('networks.statusFilter')}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {statusOptions.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={selectedStatuses.has(status)}
                    onCheckedChange={(checked) => {
                      setStatuses(
                        checked
                          ? [...filter.statuses, status]
                          : filter.statuses.filter((current) => current !== status),
                      );
                    }}
                  >
                    <span className="flex min-w-0 flex-1 justify-between gap-3">
                      <span>{statusLabel(status)}</span>
                      {counts.data ? (
                        <span className="text-muted-foreground">{counts.data[status] ?? 0}</span>
                      ) : null}
                    </span>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {canCreate ? (
              <Button
                onClick={() => {
                  void navigate({ to: '/networks/create' });
                }}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {dashboardT('networks.create')}
              </Button>
            ) : null}
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: filter.statuses.length ? (query.data?.totalElements ?? 0) : 0,
          },
          pageSizes: networkPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            setPagination(next.page, next.pageSize);
          },
        }}
      />
    </main>
  );
}

const reportMetricByNetworkCpmMetric: Record<NetworkCpmMetric, ReportMetric> = {
  [NetworkCpmMetric.AdRequests]: ReportMetric.AdRequests,
  [NetworkCpmMetric.FilledImpressions]: ReportMetric.FilledImpressions,
  [NetworkCpmMetric.AdImpressions]: ReportMetric.AdImpressions,
};

function formatCpmMetric(metric: NetworkCpmMetric | null | undefined) {
  return metric ? reportMetricLabel(reportMetricByNetworkCpmMetric[metric]) : '-';
}

function statusLabel(status: NetworkStatus) {
  return dashboardT(
    status === NetworkStatus.Active ? 'networks.status.active' : 'networks.status.archived',
  );
}

function statusFilterLabel(statuses: NetworkStatus[]) {
  if (statuses.length === statusOptions.length) return dashboardT('networks.status.all');
  if (statuses.length === 0) return dashboardT('networks.status.none');
  return statuses.map(statusLabel).join(', ');
}

function costTypeLabel(costType: NetworkCostType | undefined) {
  if (!costType) return '-';
  return dashboardT(
    costType === NetworkCostType.Cpm ? 'networks.cost.cpm' : 'networks.cost.revenueShare',
  );
}

function formatCostValue(network: Network) {
  if (network.saasCost?.value === null || network.saasCost?.value === undefined) return '-';
  return network.saasCost.costType === NetworkCostType.Cpm
    ? formatUsd(network.saasCost.value)
    : numberFormat({ style: 'percent', maximumFractionDigits: 2 }).format(network.saasCost.value);
}
